<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="w-full max-w-lg bg-gray-900 text-white rounded-lg p-6 flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <h3 class="text-xl font-bold text-indigo-300">แก้ไขลายสัก</h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-white">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>

      <form @submit.prevent="updateTattoo" class="flex flex-col gap-4">
        <div>
          <label class="text-sm text-gray-300">ชื่อลายสัก</label>
          <input v-model="form.name" type="text" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div>
          <label class="text-sm text-gray-300">URL รูปภาพ</label>
          <input v-model="form.imageUrl" type="url" class="w-full bg-gray-800 rounded-lg p-2 text-white" />
        </div>
        <div>
          <label class="text-sm text-gray-300">ประเภทลายสัก</label>
          <select v-model="form.type" class="w-full bg-gray-800 rounded-lg p-2 text-white" required>
            <option value="str">STR</option>
            <option value="stamina">Stamina</option>
            <option value="int">INT</option>
            <option value="agi">AGI</option>
            <option value="luck">LUCK</option>
          </select>
        </div>
        <div>
          <label class="text-sm text-gray-300">ลำดับ (Order)</label>
          <input v-model.number="form.order" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div>
          <label class="text-sm text-gray-300">ระดับที่ต้องการ (Level Required)</label>
          <input v-model.number="form.levelRequired" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-sm text-gray-300">ราคา (Cash)</label>
            <input v-model.number="form.cost.cash" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Diamond)</label>
            <input v-model.number="form.cost.diamond" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Gold)</label>
            <input v-model.number="form.cost.gold" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
          <div>
            <label class="text-sm text-gray-300">ราคา (Silver)</label>
            <input v-model.number="form.cost.silver" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
          </div>
        </div>
        <div>
          <label class="text-sm text-gray-300 font-bold">สถานะ</label>
          <div class="grid grid-cols-3 gap-2 mt-2">
            <div>
              <label class="text-sm text-gray-300">STR</label>
              <input v-model.number="form.stats.str" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">Stamina</label>
              <input v-model.number="form.stats.stamina" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">INT</label>
              <input v-model.number="form.stats.int" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">AGI</label>
              <input v-model.number="form.stats.agi" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
            <div>
              <label class="text-sm text-gray-300">LUCK</label>
              <input v-model.number="form.stats.luck" type="number" min="0" class="w-full bg-gray-800 rounded-lg p-2 text-white" required />
            </div>
          </div>
        </div>
        <button
          type="submit"
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
          :disabled="isUpdating"
        >
          <span v-if="!isUpdating"><i class="fas fa-save mr-2"></i> บันทึกการเปลี่ยนแปลง</span>
          <div v-else class="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
        </button>
      </form>

      <p v-if="error" class="text-red-500 text-sm text-center">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  name: 'EditTattooModal',
  props: {
    isOpen: Boolean,
    tattoo: Object
  },
  emits: ['close', 'tattoo-updated'],
  setup(props, { emit }) {
    const form = ref({
      name: '',
      imageUrl: '',
      type: 'str', // Default to 'str'
      order: 0,
      levelRequired: 0,
      cost: { cash: 0, diamond: 0, gold: 0, silver: 0 },
      stats: { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 }
    });
    const isUpdating = ref(false);
    const error = ref('');

    // Watch for changes in the tattoo prop to populate the form
    watch(
      () => props.tattoo,
      (newTattoo) => {
        if (newTattoo) {
          form.value = {
            name: newTattoo.name || '',
            imageUrl: newTattoo.imageUrl || '',
            type: newTattoo.type || 'str', // Default to 'str' if type is not set
            order: newTattoo.order || 0,
            levelRequired: newTattoo.levelRequired || 0,
            cost: {
              cash: newTattoo.cost.cash || 0,
              diamond: newTattoo.cost.diamond || 0,
              gold: newTattoo.cost.gold || 0,
              silver: newTattoo.cost.silver || 0
            },
            stats: {
              str: newTattoo.stats.str || 0,
              stamina: newTattoo.stats.stamina || 0,
              int: newTattoo.stats.int || 0,
              agi: newTattoo.stats.agi || 0,
              luck: newTattoo.stats.luck || 0
            }
          };
        }
      },
      { immediate: true }
    );

    const updateTattoo = async () => {
      isUpdating.value = true;
      error.value = '';
      try {
        await projectFirestore.collection('tattoos').doc(props.tattoo.id).update({
          name: form.value.name,
          imageUrl: form.value.imageUrl || 'default-tattoo.png',
          type: form.value.type,
          order: form.value.order,
          levelRequired: form.value.levelRequired,
          cost: form.value.cost,
          stats: form.value.stats
        });
        emit('tattoo-updated');
        emit('close');
      } catch (err) {
        console.error('Error updating tattoo:', err);
        error.value = 'เกิดข้อผิดพลาดในการแก้ไขลายสัก';
      } finally {
        isUpdating.value = false;
      }
    };

    return { form, isUpdating, error, updateTattoo };
  }
};
</script>